import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, of, throwError, catchError, tap, filter, take, map, switchMap, forkJoin } from 'rxjs';
import { PaginationService } from '../pagination.service';
import { EntityType } from '../../objects/interface-library';
import { Baa } from '../../objects/baa';

@Injectable({
  providedIn: 'root'
})
export class BaaService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);

  private allBaasSubject = new BehaviorSubject<any[]>([]);
  public readonly allBaas$: Observable<any[]> = this.allBaasSubject.asObservable();

  private visibleBaasSubject = new BehaviorSubject<any[]>([]);
  public readonly visibleBaas$: Observable<any[]> = this.visibleBaasSubject.asObservable();

  constructor() {
    // Auto-refresh BAAs on service construction (like ContractService)
    this.refreshAllBaas();
    this.setupVisibilityFiltering();
  }

  private setupVisibilityFiltering(): void {
    this.allBaas$.subscribe(baas => {
      // For now, just pass through all BAAs
      // TODO: Add permissions filtering when ready
      this.visibleBaasSubject.next(baas);
    });
  }

  /**
   * Refreshes all BAAs by calling the paginated API and updating the subject.
   * Uses POST method with lastEvaluatedKey in the body (matching Lambda handler).
   */
  refreshAllBaas(): void {
    const getPageOfBaas = (lastEvaluatedKey: any = null): Observable<any> => {
      const url = environment.baaGetAllUrl;
      const body = { lastEvaluatedKey };

      return this.http.post(url, body).pipe(
        map((response: any) => ({
          items: response.items || [],
          lastEvaluatedKey: response.lastEvaluatedKey
        })),
        catchError(error => {
          console.error('Error fetching BAA page:', error);
          return of({ items: [], lastEvaluatedKey: null });
        })
      );
    };

    this.paginationService.getAllPaginatedDataProgressively(getPageOfBaas)
      .pipe(
        catchError(error => {
          console.error('Error in getAllPaginatedData:', error);
          this.allBaasSubject.next([]);
          return of([]);
        })
      )
      .subscribe(allBaas => {
        const filteredObjects = allBaas.map((obj: any) => obj.baaData || obj);
        // console.log('BaaService: refreshAllBaas completed, count:', filteredObjects.length);
        this.allBaasSubject.next(filteredObjects);
      });
  }

  /**
   * Gets a specific BAA from the in-memory cache by its ID.
   * This is what ConnectionsService will use to get a fully populated object.
   * @param baaId The ID of the BAA to retrieve.
   * @returns An observable of the found BAA or null if not found.
   */
  public getBaaObject(baaId: string): Observable<any | null> {
    return this.allBaas$.pipe(
      map(baas => {
        const found = baas.find(baa => baa.baaId === baaId || baa.baaNum === baaId);
        if (!found) {
          console.warn('[BaaService:Diag] getBaaObject - not found for:', baaId, '| Cache size:', baas.length);
        }
        return found || null;
      }),
      take(1)
    );
  }

  /**
   * Gets a specific BAA from the in-memory cache by its baaNum.
   * @param baaNum The baaNum of the BAA to retrieve.
   * @returns An observable of the found BAA or null if not found.
   */
  public getBaaByNumber(baaNum: string): Observable<any | null> {
    return this.allBaas$.pipe(
      map(baas => {
        const foundBaa = baas.find(baa => baa.baaNum === baaNum);
        if (foundBaa) {
          return foundBaa.baaData || foundBaa;
        } else {
          console.warn('BaaService | getBaaByNumber | Did not find BAA');
          return null;
        }
      }),
      take(1)
    );
  }

  /**
   * Gets a fully-hydrated BAA object by its ID.
   * @param baaId The ID of the BAA to retrieve.
   * @returns An observable of the fully-hydrated BAA or null if not found.
   */
  public getBaaObjectFullyHydrated(baaId: string): Observable<any | null> {
    // TODO: Add full hydration when ConnectionsService is ready
    return this.getBaaObject(baaId).pipe(
      catchError(error => {
        console.error('Error in getBaaObjectFullyHydrated:', error);
        return of(null);
      })
    );
  }

  /**
   * Refreshes all BAAs and returns an Observable that completes when the refresh is done.
   * This is useful when you need to wait for the refresh before performing another action.
   */
  private refreshAllBaasAsync(): Observable<any[]> {
    const getPageOfBaas = (lastEvaluatedKey: any = null): Observable<any> => {
      const url = environment.baaGetAllUrl;
      const body = { lastEvaluatedKey };

      return this.http.post(url, body).pipe(
        map((response: any) => ({
          items: response.items || [],
          lastEvaluatedKey: response.lastEvaluatedKey
        })),
        catchError(error => {
          console.error('Error fetching BAA page in refreshAllBaasAsync:', error);
          return of({ items: [], lastEvaluatedKey: null });
        })
      );
    };

    return this.paginationService.getAllPaginatedDataProgressively(getPageOfBaas)
      .pipe(
        map(allBaas => {
          const filteredObjects = allBaas.map((obj: any) => obj.baaData || obj);
          this.allBaasSubject.next(filteredObjects);
          return filteredObjects;
        }),
        catchError(error => {
          console.error('Error in refreshAllBaasAsync:', error);
          this.allBaasSubject.next([]);
          return of([]);
        })
      );
  }

  /**
   * Writes a BAA object to the server.
   * @param baa The BAA object to write.
   * @param state The state of the BAA.
   * @returns An observable of the API response.
   */
  public writeBaa(baa: Baa): Observable<any> {
    const url = environment.baaWriteUrl;
    const body = {
      baaNum: baa.baaNum,
      baaData: baa,
      baaId: baa.baaId,
    };
    return this.http.put(url, body).pipe(
      switchMap(response => {
        return this.refreshAllBaasAsync().pipe(
          map(() => response)
        );
      }),
      catchError(error => {
        console.error('Error writing BAA:', error);
        return throwError(() => new Error('Could not write BAA.'));
      })
    );
  }

  /**
   * Updates a single attribute of a BAA.
   * Endpoint: PUT /api/{org}/update-baa-attribute
   * Body: { baaNum: string, attribute: string, value: any }
   *
   * @param baaNum The BAA number/ID to update.
   * @param attribute The attribute name to update.
   * @param attributeValue The new value for the attribute.
   * @returns An observable of the API response.
   */
  public updateBaaAttribute(baaNum: any, attribute: any, attributeValue: any): Observable<any> {
    const url = environment.URL_BAA_UPDATE_ATTRIBUTE;
    const body = { baaNum, attribute, value: attributeValue };
    return this.http.put(url, body).pipe(
      tap(() => this.refreshAllBaas()), // Refresh cache after update
      catchError(error => {
        console.error('Error updating BAA attribute:', error);
        return throwError(() => new Error('Could not update BAA attribute.'));
      })
    );
  }
}